import { FC, Props } from "./typings";
import styles from "./styles.module.scss";
import CloseIcon from "assets/icons/close.svg?react";
import { useNavigate } from "react-router-dom";
import { IAttachment } from "dto/IAttachment";
import { useContext, useEffect, useState } from "react";
import listService from "api/listService";
import PackagesContext from "contexts/PackagesContext";
import PackageAttachment from "components/PackageAttachment";
import Button from "components/Button";
import { Can } from "contexts/AbilityContext";
import usePDF from "hooks/usePDF";
import AttachToCaseModalAttachments from "components/AttachCaseModalNew/AttachToCaseModalAttachments";
import { IAttachToCaseFolderLink } from "api/casesService";
import ModalLayout, { DockPosition } from "components/ModalLayout";
import settingsContext, { IOptionType } from "contexts/SettingsContext";
import { randomString } from "utils/randomString";
import { IPackage } from "dto/IPackage";
import { EOrder, ETypeOfOrder } from "components/SortOrder/typings";
import PackageThreadContext from "contexts/PackageThreadContext";
import KeysContext from "contexts/KeysContext";
import { useTranslation } from "react-i18next";

const PackageAttachmentsModal: FC<Props> = ({
  toggle,
  pack,
  onPreview,
  previewData,
  wholeThread,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mutate: manageDossier } = listService.useManageDossier();
  const { packages, setPackages } = useContext(PackagesContext);
  const { threadPackages, setThreadPackages } =
    useContext(PackageThreadContext);
  const { setKeys } = useContext(KeysContext);

  const {
    data: packageData,
  } = listService.useGetPackage(
    {
      ThreadId: pack?.Thread?.Id,
      IncludeRemoved: true,
      enabled: wholeThread && !!pack.Thread?.Id,
    },
    {
      type: ETypeOfOrder.SEND_DATE,
      order: EOrder.DESCENDING,
    },
  );

  useEffect(() => {
    if (!packageData?.data) return;
    setThreadPackages(packageData.data.Items ?? []);
    const newKeys = [packageData.data.ObjectKeys, packageData.data.Keys].flat();
    setKeys((prev) => [...prev, ...newKeys]);
  }, [packageData]);

  const { download } = usePDF();

  const onDownloadInternal = async (
    pack: IPackage,
    attachment: IAttachment,
  ) => {
    await download(pack.Id, attachment, pack.Kind);
    return;
  };

  const [attachmentsLocation, setAttachmentsLocation] =
    useState<string>("center");

  const { options } = useContext(settingsContext);

  useEffect(() => {
    if (options) {
      const attL = options.find(
        (x) => x.Type === IOptionType.caseAttachAttachmentsLocation,
      );

      if (attL) {
        setAttachmentsLocation(attL.Value);
      }
    }
  }, [options]);

  const { checkIsAllowed, showNotSupportedNotification } = usePDF();
  const [showDossier, setShowDossier] = useState<boolean>(false);
  const [usedAttachments, setUsedAttachments] = useState<
    IAttachToCaseFolderLink[]
  >([]);
  const [unusedAttachments, setUnusedAttachments] = useState<IAttachment[]>([]);

  const onNewWindow = async (attach: IAttachment) =>
    window.open(
      `${
        import.meta.env.VITE_APP_SITE ?? "https://alfa.pocztaprawnicza.pl"
      }/document?pdf=${pack.Id}:${attach.FileName.replace(":", "")}:${
        attach.Uri ?? attach.Id
      }:${pack.Kind}:${attach.EncryptionKeyId}`,
    );

  const handleClick = () => {
    setUsedAttachments([]);
    setUnusedAttachments(pack.Attachments);
    setShowDossier(true);
  };

  const handleDossierSave = () => {
    manageDossier({
      AttachmentIds: usedAttachments.map((x) => x.AttachmentId),
      OwnerId: pack.Id,
      OwnerKind: pack.Kind,
      Show: true,
      Links: usedAttachments,
    });

    const newPacks = [...packages];

    usedAttachments.forEach((a) => {
      const el = newPacks.find((p) => p.Id === pack.Id);
      if (el) {
        const attach = el.Attachments.find((e) => e.Id === a.AttachmentId);

        if (attach) {
          attach.ShowInDossier = 1;
        }
      }
    });

    setPackages(newPacks);
  };

  return (
    <>
      {!showDossier && (
        <div className={styles.wrapper}>
          <div className={styles.content}>
            <div className={styles.title}>
              {!wholeThread ? t("documents.attachments") : t("documents.threadAttachments")}
              <CloseIcon
                className={styles.closeIcon}
                onClick={(e) => {
                  toggle(false);
                  e.stopPropagation();
                }}
              />
            </div>
            <div className={styles.list}>
              {(!wholeThread
                ? pack.Attachments
                : threadPackages
                    .filter((x) => x.Attachments)
                    .map((x) => x.Attachments)
                    .flat()
              ).map((attach) => (
                <PackageAttachment
                  className={styles.attachment}
                  key={attach.Id + randomString()}
                  onDownload={(attach) => {
                    if (previewData) {
                      onDownloadInternal(pack, attach);
                      //toggle(false);
                      return;
                    }

                    onDownloadInternal(pack, attach);
                    //toggle(false);
                  }}
                  onPreview={(attach) => {
                    if (onPreview) {
                      if (wholeThread) {
                        const p = threadPackages.find(
                          (x) =>
                            x.Attachments &&
                            x.Attachments.indexOf(attach) !== -1,
                        );
                        onPreview(p, attach, attach.EncryptionKeyId);
                      } else {
                        onPreview(pack, attach, attach.EncryptionKeyId);
                      }
                      toggle(false);
                      return;
                    }
                    const isAllowed = checkIsAllowed(attach.FileName);
                    if (!isAllowed) {
                      showNotSupportedNotification();
                      return;
                    }

                    let newUrl = `${window.location.pathname}?pdf=${
                      pack.Id
                    }:${attach.FileName.replace(":", "")}:${
                      attach.Uri ?? attach.Id
                    }:${attach.Kind}:${attach.EncryptionKeyId}`;
                    const isFullScreen =
                      window.location.href.search("fullscreen") !== -1;
                    if (isFullScreen) {
                      newUrl = newUrl + "&fullscreen=1";
                    }
                    navigate(newUrl);
                    toggle(false);
                  }}
                  onNewWindow={onNewWindow}
                  attachment={attach}
                  // isInDossier={Boolean(attach.ShowInDossier)}
                  // onAddDossier={!!pack?.Case ? handleAddOnePackage : undefined}
                />
              ))}
            </div>
            <Can I="accessModule" an="drive">
              {!!pack.Case && (
                <div className={styles.buttonWrapper}>
                  <Button onClick={handleClick} text={t("packageAttachmentsModal.addFilesToDossier")} />
                </div>
              )}
            </Can>
          </div>
        </div>
      )}
      {showDossier && (
        <>
          {attachmentsLocation === "center" && (
            <ModalLayout dock={DockPosition.CENTER} showBg={true}>
              <AttachToCaseModalAttachments
                toggle={(c) => setShowDossier(c)}
                c={pack.Case}
                pack={pack}
                unusedAttachments={unusedAttachments}
                setUnusedAttachments={setUnusedAttachments}
                setUsedAttachments={setUsedAttachments}
                usedAttachments={usedAttachments}
                onSave={handleDossierSave}
                position="center"
              ></AttachToCaseModalAttachments>
            </ModalLayout>
          )}
          {attachmentsLocation !== "center" && (
            <ModalLayout
              dock={DockPosition.DISABLED}
              off={true}
              customX={8}
              customY={0}
              ignore
              isAnimation
              fullHeight={true}
              name="attachCase"
            >
              <div className={styles.leftModal}>
                <AttachToCaseModalAttachments
                  toggle={(c) => setShowDossier(c)}
                  c={pack.Case}
                  pack={pack}
                  unusedAttachments={unusedAttachments}
                  setUnusedAttachments={setUnusedAttachments}
                  setUsedAttachments={setUsedAttachments}
                  usedAttachments={usedAttachments}
                  onSave={handleDossierSave}
                  className={styles.nomodal}
                  position="left"
                ></AttachToCaseModalAttachments>
              </div>
            </ModalLayout>
          )}
        </>
      )}
    </>
  );
};

export default PackageAttachmentsModal;
